import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import type { CreateVisitResponseData } from "@/common/types/visits";
import { refreshOwnUsage } from "@/lib/usage-queries";
import { getVisitScope, refreshVisitReads } from "@/lib/visit-queries";
import {
  beginVisitDraft,
  clearPendingVisitSubmissions,
  clearVisitSubmission,
  completeVisitSubmission,
  hasPendingVisitSubmission,
  markVisitAudioQueued,
  prepareVisitSubmission,
  readVisitSubmission,
  type VisitDraftBody,
} from "@/lib/visit-submission";
import { createVisit, VisitRejectedError } from "@/services/visits";
import { useAuthStore } from "@/store/auth.store";
import { useBackgroundVisitUploadStore } from "@/store/backgroundVisitUpload.store";
import { usePostOnboardingStore } from "@/store/postOnboarding.store";

const inFlight = new Map<string, Promise<CreateVisitResponseData>>();
// Hooks re-read the stored submission when a request in this tab settles,
// so a request that failed shows its recovery notice right away.
const settleListeners = new Set<() => void>();
// A request of this account is still running in this tab: its stored
// "pending" submission is the current one, not a previous one left behind.
const submittingHere = (account: string): boolean => {
  for (const key of inFlight.keys())
    if (key.startsWith(`${account}:`)) return true;
  return false;
};
const message = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : t("visit.recovery.submissionUncertain");

export const useVisitSubmit = ({
  draftId = "recorder",
}: {
  draftId?: string;
} = {}): {
  submitVisit: (body: VisitDraftBody, audioBlob: Blob) => Promise<boolean>;
  submitVisitWithoutFile: (body: VisitDraftBody) => Promise<boolean>;
  beginDraft: () => boolean;
  abandon: () => void;
  recovery: boolean;
  canRetry: boolean;
  result: boolean;
  // The visit the last successful submission created (or found).
  visitPublicId: string | null;
  loading: boolean;
  error: string | null;
} => {
  const accountPublicId = useAuthStore((state) => state.userData?.public_id);
  const uid = useAuthStore((state) => state.user?.uid);
  const account = uid && accountPublicId ? `${uid}:${accountPublicId}` : null;
  const readScope = getVisitScope();
  const scope = `${account}:${draftId}`;
  const activeScope = useRef<{
    scope: string;
    readScope: typeof readScope;
  } | null>({ scope, readScope });
  if (
    activeScope.current?.scope !== scope ||
    activeScope.current.readScope !== readScope
  )
    activeScope.current = { scope, readScope };
  const lifetime = activeScope.current;
  const [, refresh] = useState(0);
  const [result, setResult] = useState(false);
  const [visitPublicId, setVisitPublicId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    activeScope.current = lifetime;
    setResult(false);
    setError(null);
    setLoading(false);
    return () => {
      if (activeScope.current === lifetime) activeScope.current = null;
    };
  }, [lifetime]);
  useEffect(() => {
    const listener = (): void => refresh((value) => value + 1);
    settleListeners.add(listener);
    return () => {
      settleListeners.delete(listener);
    };
  }, []);
  // "A previous submission is unresolved": a pending submission that no
  // request in this tab is still working on (a lost response, a reload, a
  // closed sheet). While the note is being written it is not shown.
  let recovery = false;
  let canRetry = false;
  let storageError: string | null = null;
  if (account) {
    try {
      recovery =
        !loading &&
        hasPendingVisitSubmission(account) &&
        !submittingHere(account);
      canRetry = readVisitSubmission(account, draftId) !== null;
    } catch (failure) {
      recovery = true;
      storageError = message(failure);
    }
  }
  const currentAccount = (): boolean =>
    activeScope.current === lifetime &&
    getVisitScope() === readScope &&
    useAuthStore.getState().user?.uid === uid &&
    useAuthStore.getState().userData?.public_id === accountPublicId;
  const submit = async (
    body: VisitDraftBody,
    audioBlob?: Blob,
  ): Promise<boolean> => {
    if (!currentAccount()) return false;
    setResult(false);
    setVisitPublicId(null);
    setLoading(true);
    setError(null);
    try {
      if (!account || !uid || !accountPublicId || !currentAccount())
        throw new Error("Sign in before submitting.");
      const prepared = await prepareVisitSubmission(account, draftId, body);
      if (!currentAccount()) {
        throw new Error(
          "Account changed. Check Visits in the original account.",
        );
      }
      const operationKey = `${account}:${draftId}`;
      let operation = inFlight.get(operationKey);
      if (!operation) {
        operation = (async (): Promise<CreateVisitResponseData> => {
          let submission = prepared;
          if (!currentAccount())
            throw new Error(
              "Account changed. Check Visits in the original account.",
            );
          const pendingKey = submission.key;
          const accepted =
            submission.kind === "accepted"
              ? { visitPublicId: submission.visitPublicId }
              : await createVisit(
                  { ...body, name: pendingKey },
                  { key: pendingKey, userUid: uid },
                ).catch((failure: unknown) => {
                  // A 400 is final and nothing was saved: release the retry
                  // identity so no "pending submission" is left to abandon.
                  if (failure instanceof VisitRejectedError) {
                    const stored = readVisitSubmission(account, draftId);
                    if (stored?.kind === "pending" && stored.key === pendingKey)
                      clearVisitSubmission(account, draftId);
                  }
                  throw failure;
                });
          if (!currentAccount())
            throw new Error(
              "Account changed. Check Visits in the original account.",
            );
          if (submission.kind === "pending") {
            submission = completeVisitSubmission(
              account,
              draftId,
              submission,
              accepted.visitPublicId,
            );
            usePostOnboardingStore.getState().markDone();
          }
          void refreshVisitReads(readScope);
          void refreshOwnUsage(readScope);
          if (
            audioBlob &&
            submission.kind === "accepted" &&
            !submission.audioQueued
          ) {
            try {
              await useBackgroundVisitUploadStore
                .getState()
                .enqueueVisitToUpload(
                  {
                    visitPublicId: accepted.visitPublicId,
                    file: audioBlob,
                    accountPublicId: accountPublicId,
                    duration: Math.ceil(body.duration),
                    language: body.language,
                    style: body.style,
                    format: body.format,
                    exam: body.exam,
                    template: body.template,
                    macroPublicIds: [],
                    customTemplatePublicId: body.customTemplatePublicId ?? null,
                  },
                  { accountUid: uid },
                );
              if (currentAccount())
                markVisitAudioQueued(account, draftId, submission);
            } catch {
              throw new Error(
                "Visit saved, but audio could not be queued. Retry to attach audio to the saved visit.",
              );
            }
          }
          return accepted;
        })();
        inFlight.set(operationKey, operation);
        void operation
          .finally(() => {
            if (inFlight.get(operationKey) === operation)
              inFlight.delete(operationKey);
            for (const listener of settleListeners) listener();
          })
          .catch(() => undefined);
      }
      const accepted = await operation;
      if (!currentAccount()) return false;
      setVisitPublicId(accepted.visitPublicId);
      setResult(true);
      return true;
    } catch (failure) {
      if (currentAccount()) setError(message(failure));
      return false;
    } finally {
      if (currentAccount() && !inFlight.has(scope)) setLoading(false);
    }
  };
  return {
    submitVisit: (body, audioBlob) => submit(body, audioBlob),
    submitVisitWithoutFile: (body) => submit(body),
    beginDraft: (): boolean => {
      try {
        if (!account) throw new Error("Sign in before recording.");
        beginVisitDraft(account, draftId);
        setError(null);
        setResult(false);
        return true;
      } catch (failure) {
        setError(message(failure));
        return false;
      }
    },
    abandon: (): void => {
      if (account && !loading) {
        clearPendingVisitSubmissions(account);
        setError(null);
        setResult(false);
        refresh((value) => value + 1);
      }
    },
    result,
    visitPublicId,
    loading,
    error: error ?? storageError,
    recovery,
    canRetry,
  };
};
